import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ClipboardList, Flame, Crown, Target, FileText, PlayCircle, Link as LinkIcon,
  StickyNote, Plus, Trash2, ExternalLink, Pencil, ChevronDown, ChevronUp,
  Quote, Sparkles, CheckCircle2, Check,
} from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { useT } from "@/lib/i18n";

const ICON_BY_SECTION = {
  procesos: ClipboardList,
  motivacion: Flame,
  liderazgo: Crown,
  estrategia: Target,
};

function getEmbedUrl(url) {
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

function ResourceCard({ resource, isAdmin, onDelete, onMarkRead, sectionColor }) {
  const Icon =
    resource.resource_type === "video" ? PlayCircle :
    resource.resource_type === "pdf" ? FileText :
    resource.resource_type === "note" ? StickyNote : LinkIcon;

  const fileSrc = resource.url_type === "url" ? resource.url : `${API}/files/${resource.url}`;

  const headerActions = (
    <div className="flex items-center gap-1">
      {!isAdmin && (
        <button
          onClick={() => onMarkRead(resource.id)}
          disabled={resource.read}
          className={`text-xs px-2.5 py-1 rounded-md inline-flex items-center gap-1 transition-colors ${
            resource.read
              ? "bg-[#1B2E7A]/10 text-[#1B2E7A] cursor-default"
              : "bg-[#F4B81F]/15 text-[#B45309] hover:bg-[#F4B81F]/25"
          }`}
          data-testid={`mark-read-${resource.id}`}
          title={resource.read ? "Leído" : "Marcar como leído (+5 pts)"}
        >
          {resource.read ? (
            <>
              <Check className="h-3.5 w-3.5" /> Leído
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" /> +5 pts
            </>
          )}
        </button>
      )}
      {isAdmin && (
        <button
          onClick={() => onDelete(resource.id)}
          data-testid={`del-kres-${resource.id}`}
          className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1.5 rounded-md transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  if (resource.resource_type === "note") {
    return (
      <div
        className="bg-white border border-[#E5E7EB] rounded-md p-5 relative"
        data-testid={`kres-${resource.id}`}
        style={{ borderLeft: `3px solid ${sectionColor}` }}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <StickyNote className="h-4 w-4" style={{ color: sectionColor }} strokeWidth={1.5} />
            <h4 className="font-heading text-base text-[#1A1A1A]">{resource.title}</h4>
          </div>
          {headerActions}
        </div>
        {resource.description && (
          <p className="text-xs text-[#737373] mb-2">{resource.description}</p>
        )}
        <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">{resource.body}</p>
      </div>
    );
  }

  if (resource.resource_type === "video") {
    const embed = getEmbedUrl(resource.url);
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`kres-${resource.id}`}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <PlayCircle className="h-4 w-4 shrink-0" style={{ color: sectionColor }} strokeWidth={1.5} />
            <h4 className="text-sm font-medium text-[#1A1A1A] truncate">{resource.title}</h4>
          </div>
          {headerActions}
        </div>
        <div className="video-shell">
          {embed ? (
            <iframe src={embed} title={resource.title} allow="autoplay; encrypted-media" allowFullScreen />
          ) : resource.url_type === "upload" ? (
            <video controls src={fileSrc} />
          ) : (
            <div className="flex items-center justify-center h-48 text-white">
              <a href={resource.url} target="_blank" rel="noreferrer" className="underline">Abrir video</a>
            </div>
          )}
        </div>
        {resource.description && (
          <p className="px-4 py-2 text-xs text-[#737373] bg-[#FFFFFF]">{resource.description}</p>
        )}
      </div>
    );
  }

  if (resource.resource_type === "pdf") {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`kres-${resource.id}`}>
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 shrink-0" style={{ color: sectionColor }} strokeWidth={1.5} />
            <h4 className="text-sm font-medium text-[#1A1A1A] truncate">{resource.title}</h4>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={fileSrc}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#1B2E7A] hover:underline inline-flex items-center gap-1 shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Abrir
            </a>
            {headerActions}
          </div>
        </div>
        <iframe src={fileSrc} title={resource.title} className="w-full" style={{ height: "60vh" }} />
        {resource.description && (
          <p className="px-4 py-2 text-xs text-[#737373] bg-[#FFFFFF]">{resource.description}</p>
        )}
      </div>
    );
  }

  // link
  return (
    <div
      data-testid={`kres-${resource.id}`}
      className="bg-white border border-[#E5E7EB] rounded-md p-4 flex items-start gap-3"
    >
      <div
        className="h-10 w-10 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${sectionColor}15`, color: sectionColor }}
      >
        <LinkIcon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-[#1A1A1A] mb-0.5">{resource.title}</h4>
        <a
          href={resource.url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#1B2E7A] hover:underline truncate block"
        >
          {resource.url}
        </a>
        {resource.description && <p className="text-xs text-[#737373] mt-1">{resource.description}</p>}
      </div>
      <div className="flex items-center gap-1">
        <a
          href={resource.url}
          target="_blank"
          rel="noreferrer"
          className="text-[#6B7280] hover:text-[#1B2E7A] p-1.5"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        {headerActions}
      </div>
    </div>
  );
}

function ResourceForm({ section, onAdded }) {
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    resource_type: "pdf",
    url_type: "upload",
    url: "",
    body: "",
  });
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, url: data.path, url_type: "upload" }));
      toast.success("Archivo subido");
    } catch {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.title) {
      toast.error("Título requerido");
      return;
    }
    if (form.resource_type !== "note" && !form.url) {
      toast.error("Sube un archivo o agrega una URL");
      return;
    }
    if (form.resource_type === "note" && !form.body) {
      toast.error("La nota necesita contenido");
      return;
    }
    try {
      await api.post(`/admin/knowledge/${section}`, form);
      toast.success("Recurso agregado");
      setForm({ title: "", description: "", resource_type: "pdf", url_type: "upload", url: "", body: "" });
      if (fileRef.current) fileRef.current.value = "";
      onAdded();
    } catch {
      toast.error("Error al agregar");
    }
  };

  const acceptByType = form.resource_type === "pdf" ? "application/pdf" : "video/*";

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-md mb-6" data-testid="resource-form">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-[#FFFFFF] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-[#1B2E7A]" />
          <span className="font-medium text-sm text-[#1A1A1A]">Subir nuevo material</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#737373]" /> : <ChevronDown className="h-4 w-4 text-[#737373]" />}
      </button>

      {open && (
        <div className="border-t border-[#E5E7EB] p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <select
              value={form.resource_type}
              onChange={(e) => setForm({
                ...form,
                resource_type: e.target.value,
                url: "",
                url_type: e.target.value === "link" ? "url" : "upload",
              })}
              className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
              data-testid="kres-type-select"
            >
              <option value="pdf">PDF</option>
              <option value="video">Video</option>
              <option value="link">Enlace</option>
              <option value="note">Nota / texto</option>
            </select>
            <Input
              placeholder="Título"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="kres-title-input"
            />
          </div>

          <Input
            placeholder="Descripción breve (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          {form.resource_type === "note" ? (
            <Textarea
              placeholder="Contenido de la nota..."
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              data-testid="kres-body-input"
            />
          ) : form.resource_type === "link" ? (
            <Input
              placeholder="URL https://..."
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              data-testid="kres-url-input"
            />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <select
                  value={form.url_type}
                  onChange={(e) => setForm({ ...form, url_type: e.target.value, url: "" })}
                  className="border border-[#E5E7EB] rounded-md px-3 py-1.5 text-xs bg-white"
                >
                  <option value="upload">Subir archivo</option>
                  <option value="url">URL externa</option>
                </select>
                {form.url_type === "upload" ? (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept={acceptByType}
                      onChange={handleUpload}
                      className="text-sm"
                      data-testid="kres-file-input"
                    />
                    {uploading && <span className="text-xs text-[#737373]">Subiendo...</span>}
                    {form.url && !uploading && <span className="text-xs text-[#1B2E7A]">Listo</span>}
                  </>
                ) : (
                  <Input
                    placeholder="URL https://... (YouTube, Vimeo, etc.)"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    className="flex-1"
                  />
                )}
              </div>
            </div>
          )}

          <Button
            onClick={submit}
            className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
            data-testid="add-kres-button"
          >
            <Plus className="h-4 w-4 mr-1" /> Agregar
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ContentHub() {
  const t = useT();
  const { section } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/knowledge/${section}`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [section]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este recurso?")) return;
    await api.delete(`/admin/knowledge/${section}/${id}`);
    toast.success("Recurso eliminado");
    load();
  };

  const markRead = async (id) => {
    try {
      const r = await api.post(`/knowledge/${section}/${id}/read`);
      if (r.data.points_awarded > 0) {
        toast.success(`+${r.data.points_awarded} puntos`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (loading) return <div className="text-[#374151]">Cargando...</div>;
  if (!data) return <div className="text-[#374151]">Sección no encontrada.</div>;

  const meta = data.section;
  const Icon = ICON_BY_SECTION[meta.key] || ClipboardList;
  const isAdmin = user?.role === "admin";

  return (
    <div data-testid={`section-${meta.key}-page`}>
      <div className="mb-8 fade-up">
        <p
          className="text-xs uppercase tracking-[0.2em] mb-2 inline-flex items-center gap-1.5"
          style={{ color: meta.color }}
        >
          <Icon className="h-3.5 w-3.5" /> {meta.subtitle}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          {meta.title}.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">{meta.description}</p>
      </div>

      {/* Daily quote */}
      {data.daily_quote && (
        <div
          className="bg-white border border-[#E5E7EB] rounded-2xl p-5 md:p-6 mb-6 relative overflow-hidden"
          data-testid="daily-quote"
          style={{ borderLeft: `4px solid ${meta.color}` }}
        >
          <Quote className="absolute -top-2 -left-2 h-16 w-16 opacity-5" style={{ color: meta.color }} />
          <p className="text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: meta.color }}>
            Frase del día
          </p>
          <p className="font-heading text-xl md:text-2xl text-[#1A1A1A] leading-snug mb-1.5">
            "{data.daily_quote.text}"
          </p>
          <p className="text-xs text-[#6B7280]">— {data.daily_quote.author}</p>
        </div>
      )}

      {/* Weekly AI reflection */}
      {data.weekly_reflection && (
        <div
          className="bg-gradient-to-br from-[#FEF3C7] to-[#FAEFD7] border border-[#F4B81F]/40 rounded-2xl p-5 md:p-6 mb-6"
          data-testid="weekly-reflection"
        >
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#B45309] mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" /> Reflexión de la semana · IA
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-2">
            {data.weekly_reflection.title}
          </h2>
          <p className="text-sm md:text-base text-[#854D0E] leading-relaxed">
            {data.weekly_reflection.body}
          </p>
        </div>
      )}

      {isAdmin && <ResourceForm section={section} onAdded={load} />}

      <SectionHeader
        eyebrow={t("content.eyebrow.materials")}
        title={t("content.title.team_resources", { section: meta.title })}
        accent={meta.color}
        testid="section-content-resources"
      />

      {data.resources.length === 0 ? (
        <div
          className="text-center py-16 bg-white border border-[#E5E7EB] rounded-md"
          data-testid="empty-state"
        >
          <Icon className="h-10 w-10 mx-auto mb-3" style={{ color: meta.color }} strokeWidth={1.2} />
          <p className="text-[#374151] mb-1">Aún no hay materiales en {meta.title}.</p>
          {isAdmin ? (
            <p className="text-xs text-[#6B7280]">Sube el primer material desde el formulario de arriba.</p>
          ) : (
            <p className="text-xs text-[#6B7280]">Pronto el equipo cargará contenido aquí.</p>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {data.resources.map((r) => (
            <ResourceCard
              key={r.id}
              resource={r}
              isAdmin={isAdmin}
              onDelete={remove}
              onMarkRead={markRead}
              sectionColor={meta.color}
            />
          ))}
        </div>
      )}
    </div>
  );
}
